import { useCallback, useEffect, useMemo, useState } from "react";

import { useToggle } from "usehooks-ts";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import { fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";

export const useDiscussionSnippet = ({
  initialDiscussion,
  parentDiscussion,
  onClick,
  onReply,
  onDiscussionPost,
  isChildren,
  showInlineComposer,
  showComposerByDefault,
  key,
}: any) => {
  const { account: loggedInAccount } = useAuthentication();
  const { setNotification } = useNotification();

  const [globalDiscussion, replaceGlobal, mergeGlobal] = useGlobalState(
    `comments-${initialDiscussion?.id}`,
    initialDiscussion
  );

  const [localDiscussion, replaceLocal, mergeLocal] = useGlobalState(
    key,
    initialDiscussion
  );

  const replace = useCallback(
    (discussion) => {
      replaceGlobal(discussion);
      replaceLocal(discussion);
    },
    [replaceGlobal, replaceLocal]
  );

  const merge = useCallback(
    (discussion) => {
      mergeGlobal(discussion);
      mergeLocal(discussion);
    },
    [mergeGlobal, mergeLocal]
  );

  const discussion = key ? localDiscussion : globalDiscussion;

  const [_, setShowingModal] = useGlobalState("showingModal", false);

  const { id, type, commenter, commentable } = discussion || {};

  const hasParent =
    discussion?.hasParent ||
    !!discussion?.parentId ||
    !!discussion?.["parent-id"];
  const bodyUpdatedAt =
    discussion?.bodyUpdatedAt || discussion?.["body-updated-at"];
  const hasBeenDeleted =
    discussion?.hasBeenDeleted || discussion?.["has-been-deleted"];
  const revisionsCount =
    discussion?.revisionsCount || discussion?.["revisions-count"];

  const isAuthor = loggedInAccount?.id == commenter?.id; // id can be string or number, hence use == instead of ===

  const isLiked =
    type === "comments" ? discussion?.isLiked : discussion?.["is-liked"];
  const likesCount =
    type === "comments" ? discussion?.likesCount : discussion?.["likes-count"];

  const [showComposer, toggleShowComposer] = useToggle(showComposerByDefault);
  const url = `${emberAppBaseUrl}/discussion/${id}`;

  const shareText = `${commenter?.name || "Someone"} commented on ${
    commentable?.tagline ||
    commentable?.prettyName ||
    commentable?.["pretty-name"] ||
    commentable?.name ||
    commentable?.["display-name"] ||
    parentDiscussion?.commentable?.tagline ||
    parentDiscussion?.commentable?.prettyName ||
    parentDiscussion?.commentable?.["pretty-name"] ||
    parentDiscussion?.commentable?.name ||
    parentDiscussion?.commentable?.["display-name"]
  }​`;

  const [showDiscussionModal, _setShowDiscussionModal] = useState(false);
  const [showEdit, toggleEdit] = useToggle();
  const [showDelete, toggleDelete] = useToggle();

  const setShowDiscussionModal = useCallback(
    (state) => {
      _setShowDiscussionModal(state);
      setShowingModal(state);
    },
    [setShowingModal]
  );

  const shareBtnOptions = useMemo(
    () => ({
      email: {
        link: `mailto:?subject=\u272a Smartkarma | ${shareText}&body=${encodeURIComponent(
          shareText
        )}.\nRead more: ${url}`,
      },
      clipboard: {
        message: url,
      },
      privateMessage: {
        message: `${shareText}: <a href="${url}">${url}</a>`,
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${shareText}: ${url}`,
      },
    }),
    [shareText, url]
  );

  const like = useCallback(() => {
    if (isAuthor) {
      return;
    }

    const action = isLiked ? "unlike" : "like";
    const updatedlikesCount = isLiked ? likesCount - 1 : likesCount + 1;
    if (type === "comments") {
      merge({
        ...discussion,
        isLiked: !isLiked,
        likesCount: updatedlikesCount,
      });
    } else {
      merge({
        ...discussion,
        "is-liked": !isLiked,
        "likes-count": updatedlikesCount,
      });
    }
    fetchApi(`/api/v2/comments/${id}/${action}`, { method: "PATCH" })
      .then(() => {})
      .catch(() => {
        const updatedlikesCount = isLiked ? likesCount + 1 : likesCount - 1;
        if (type === "comments") {
          merge({
            ...discussion,
            isLiked: !isLiked,
            likesCount: updatedlikesCount,
          });
        } else {
          merge({
            ...discussion,
            "is-liked": !isLiked,
            "likes-count": updatedlikesCount,
          });
        }
      });
  }, [isAuthor, isLiked, likesCount, type, id, merge, discussion]);

  const reply = useCallback(() => {
    if (isChildren) return onReply?.();

    if (!showInlineComposer) return setShowDiscussionModal(true);

    toggleShowComposer();
  }, [
    isChildren,
    onReply,
    setShowDiscussionModal,
    showInlineComposer,
    toggleShowComposer,
  ]);

  const onMessage = useCallback(
    (event) => {
      const messageType = event?.data[0];
      switch (messageType) {
        case "discussion-compose-success-post": {
          onDiscussionPost && onDiscussionPost();
          return;
        }
        case "discussion-compose-on-post": {
          return;
        }
      }
    },
    [onDiscussionPost]
  );

  const onDelete = useCallback(() => {
    fetchApi(`/api/v2/comments/${discussion?.id}/remove`, {
      method: "PATCH",
    })
      .then((removedComment) => {
        replace({ ...removedComment, children: discussion?.children });
      })
      .catch(() => {
        setNotification("Something went wrong!", "error");
      })
      .finally(() => {
        toggleDelete();
      });
  }, [
    discussion?.children,
    discussion?.id,
    replace,
    setNotification,
    toggleDelete,
  ]);

  const onClickCard = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).tagName === "A") {
        return;
      }
      if (onClick) {
        return onClick(discussion, e);
      }
      e.stopPropagation();
      e.preventDefault();
      setShowDiscussionModal(true);
    },
    [discussion, onClick, setShowDiscussionModal]
  );

  const onToogleEdit = useCallback(() => {
    toggleEdit();
  }, [toggleEdit]);

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  return {
    discussion,
    merge,
    replace,

    hasParent,
    bodyUpdatedAt,
    hasBeenDeleted,
    revisionsCount,

    isAuthor,
    isLiked,
    likesCount,

    showComposer,
    showDiscussionModal,
    showEdit,
    showDelete,

    toggleShowComposer,
    setShowDiscussionModal,
    toggleEdit: onToogleEdit,
    toggleDelete,
    onClickCard,

    shareBtnOptions,
    like,
    reply,
    onDelete,
  };
};
